import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import {
  Basket,
  CheckoutStoreFacade,
  KnownBasketItemPaymentMethodType,
  VoucherCodeActions,
} from '@modules/checkout-store';
import { RouteNames } from '@nx-ng/constants';
import { IBasketItemWithDescription } from '@nx-ng/types';
import { TicketDescriptionModalService } from '@nx-ng/services/ticket-description-modal.service';
import { Actions, ofType } from '@ngrx/effects';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Component({
  selector: 'nx-ng-basket-modal',
  templateUrl: './basket-modal.component.html',
  styleUrls: ['./basket-modal.component.scss'],
})
export class BasketModalComponent implements OnInit, OnDestroy {
  constructor(
    public dialogRef: MatDialogRef<BasketModalComponent>,
    private router: Router,
    public checkoutFacade: CheckoutStoreFacade,
    private ticketDescriptionModalService: TicketDescriptionModalService,
    private actions$: Actions
  ) {}

  public paymentMethodType = KnownBasketItemPaymentMethodType;

  private DEFAULT_DISCOUNT = {
    voucherCode: '',
    loading: false,
    error: '',
    applied: false,
    expanded: false
  };

  public discount = {
    ...this.DEFAULT_DISCOUNT
  }

  private unsubscribe$: Subject<void> = new Subject<void>();

  ngOnInit() {
    this.checkoutFacade.selectBasket$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((basket?: Basket) => {
        if (basket && basket.discount) {
          this.discount = {
            ...this.DEFAULT_DISCOUNT,
            voucherCode: basket.discount.voucherCode,
            error: basket.discount.discountRejectedReason || '',
            applied: !basket.discount.discountRejectedReason,
            expanded: true
          };
        }
      })

    this.actions$.pipe(
      ofType(VoucherCodeActions.discountAPI_applyVoucherCodeSuccess),
      takeUntil(this.unsubscribe$)).subscribe((success) => {
        this.discount = { ...this.DEFAULT_DISCOUNT, voucherCode: success.voucherCode, applied: true, expanded: true };
      });

    this.actions$.pipe(
      ofType(VoucherCodeActions.discountAPI_applyVoucherCodeFailure, VoucherCodeActions.discountAPI_removeVoucherCodeFailure),
      takeUntil(this.unsubscribe$)).subscribe((error) => {
        this.discount = { ...this.DEFAULT_DISCOUNT, error: error.errorMsg, voucherCode: error.voucherCode, expanded: true, applied: this.discount.applied };
      });

    this.actions$.pipe(
      ofType(VoucherCodeActions.discountAPI_removeVoucherCodeSuccess),
      takeUntil(this.unsubscribe$)).subscribe(() => {
        this.discount = { ...this.DEFAULT_DISCOUNT, expanded: true };
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
  }

  routeToTicketTypes = (event?: Event) => {
    this.dialogRef.close();
    this.router.navigate([RouteNames.TICKET_TYPES]);
    event?.preventDefault();
  };

  submit() {
    this.dialogRef.close();
    this.router.navigate([RouteNames.BOOKING_SUMMARY]);
  }

  trackByFn(_index: number, item: IBasketItemWithDescription) {
    return item.ticketID;
  }

  handleQuantityUpdated(item: IBasketItemWithDescription, quantity: number) {
    this.checkoutFacade.updateItemQuantityInBasket({
      ticketID: item.ticketID,
      quantity,
    });
  }

  handleDiscountApplied(basketId: string, voucherCode: string) {
    this.discount.error = '';
    this.discount.loading = true;
    this.checkoutFacade.applyDiscount(basketId, voucherCode)
  }

  handleDiscountRemoved(basketId: string, voucherCode: string) {
    this.discount.loading = true;
    this.checkoutFacade.removeDiscount(basketId, voucherCode)
  }

  handleConvertToSubscription(item: IBasketItemWithDescription) {
    this.checkoutFacade.convertItemToSubscription({
      ticketID: item.ticketID,
      quantity: item.quantity,
    });
  }

  handleShowInfoClicked(item: IBasketItemWithDescription) {
    this.ticketDescriptionModalService.showModal(item.alias, item.title);
  }
}
